import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Monitor,
  Cpu,
  HardDrive,
  MemoryStick,
  Zap,
  Settings,
} from "lucide-react";
import Navbar from "../../../sub-comp/Navbar";
import hardware from "../../../../assets/computer-hardware.jpg";
import FullScreenImage from "../../../Home/ShowcaseSection";
import Footer from "../../../sub-comp/MainFooter";

const ComputerHardware = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleModules, setVisibleModules] = useState([]);

  useEffect(() => {
    // Trigger main animations after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    // Stagger module animations
    const moduleTimer = setTimeout(() => {
      const modules = Array.from({ length: 12 }, (_, i) => i);
      modules.forEach((index) => {
        setTimeout(() => {
          setVisibleModules((prev) => [...prev, index]);
        }, index * 100);
      });
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearTimeout(moduleTimer);
    };
  }, []);

  const learningItems = [
    {
      icon: <Cpu className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Processors & CPUs",
      description:
        "Understanding CPU architecture, instruction sets, and performance optimization.",
    },
    {
      icon: <MemoryStick className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Memory Systems",
      description: "RAM types, memory hierarchy, and storage technologies.",
    },
    {
      icon: <HardDrive className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Storage Devices",
      description: "HDDs, SSDs, and emerging storage technologies.",
    },
    {
      icon: <Monitor className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Display Systems",
      description: "Graphics cards, monitors, and display technologies.",
    },
    {
      icon: <Zap className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Power Systems",
      description: "Power supplies, voltage regulation, and energy efficiency.",
    },
    {
      icon: <Settings className="w-8 h-8 text-custom-pink mb-4" />,
      title: "System Assembly",
      description:
        "Building, configuring, and troubleshooting complete systems.",
    },
  ];

  const modules = [
    "Introduction to Computer Hardware",
    "Motherboards and System Architecture",
    "Processors and CPU Technologies",
    "Memory Systems and RAM",
    "Storage Technologies",
    "Power Supply and Cooling Systems",
    "Input/Output Devices and Peripherals",
    "Graphics and Display Systems",
    "System Assembly and Configuration",
    "Hardware Troubleshooting and Repair",
    "Hardware Compatibility and Upgrades",
    "Industry Standards and Certifications",
  ];

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="relative h-[42rem] bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={hardware}
              alt="Computer Hardware Components"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 transition-opacity duration-500"></div>
          </div>

          <div className="relative z-10 flex items-center justify-center h-full">
            <div
              className={`text-center text-white transform transition-all duration-1000 ease-out ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-8 opacity-0"
              }`}
            >
              <h1 className="text-5xl md:text-6xl font-bold text-red-100 animate-pulse">
                Computer Hardware
              </h1>
              <p
                className={`text-xl mt-4 opacity-90 transform transition-all duration-1000 delay-300 ${
                  isVisible
                    ? "translate-y-0 opacity-90"
                    : "translate-y-4 opacity-0"
                }`}
              >
                Master the fundamentals of computer components and systems
              </p>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <Link
              to="/course/information-technology"
              className="flex items-center text-custom-pink hover:text-custom-pink/70 font-medium transition-all duration-300 hover:translate-x-1 group"
            >
              <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:-translate-x-1" />
              Back to IT Programs
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="md:col-span-2">
              {/* Program Overview */}
              <section
                className={`mb-12 transform transition-all duration-800 ${
                  isVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
              >
                <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                  Program Overview
                  <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink transform origin-left transition-transform duration-500 delay-500 scale-x-100"></span>
                </h2>
                <div className="prose prose-lg text-gray-600 space-y-4">
                  <p
                    className={`transform transition-all duration-700 delay-300 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-4 opacity-0"
                    }`}
                  >
                    Our Computer Hardware program provides comprehensive
                    training in the physical components that make up computer
                    systems. Students will gain hands-on experience with
                    processors, memory, storage devices, motherboards, and
                    peripheral components.
                  </p>
                  <p
                    className={`transform transition-all duration-700 delay-500 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-4 opacity-0"
                    }`}
                  >
                    This program is designed for individuals who want to
                    understand how computers work at the hardware level,
                    troubleshoot hardware issues, and pursue careers in computer
                    repair, system building, and technical support.
                  </p>
                </div>
              </section>

              {/* What You'll Learn */}
              <section
                className={`mb-12 transform transition-all duration-800 delay-200 ${
                  isVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
              >
                <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                  What You'll Learn
                  <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink transform origin-left transition-transform duration-500 delay-700 scale-x-100"></span>
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {learningItems.map((item, index) => (
                    <div
                      key={index}
                      className={`bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 group cursor-pointer transform ${
                        isVisible
                          ? "translate-y-0 opacity-100"
                          : "translate-y-8 opacity-0"
                      }`}
                      style={{
                        transitionDelay: `${800 + index * 150}ms`,
                      }}
                    >
                      <div className="transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-semibold mb-3 transition-colors duration-300 group-hover:text-custom-pink">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Course Modules */}
              <section
                className={`mb-12 transform transition-all duration-800 delay-400 ${
                  isVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
              >
                <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                  Course Modules
                  <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink transform origin-left transition-transform duration-500 delay-900 scale-x-100"></span>
                </h2>
                <div className="space-y-4">
                  {modules.map((module, index) => (
                    <div
                      key={index}
                      className={`bg-white p-4 rounded-lg shadow-sm border-l-4 border-custom-pink hover:shadow-md hover:border-l-8 transition-all duration-500 hover:-translate-y-1 hover:translate-x-2 group cursor-pointer transform ${
                        visibleModules.includes(index)
                          ? "translate-y-0 opacity-100 scale-100"
                          : "translate-y-4 opacity-0 scale-95"
                      }`}
                    >
                      <h3 className="font-semibold text-gray-800 transition-colors duration-300 group-hover:text-custom-pink">
                        Module {index + 1}: {module}
                      </h3>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div
              className={`md:col-span-1 transform transition-all duration-1000 delay-600 ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-8 opacity-0"
              }`}
            >
              {/* Program Details */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group">
                <h3 className="text-xl font-bold text-gray-800 mb-4 transition-colors duration-300 group-hover:text-custom-pink">
                  Program Details
                </h3>
                <div className="space-y-3">
                  {[
                    { label: "Duration:", value: "6 months" },
                    { label: "Format:", value: "Hands-on Lab + Theory" },
                    { label: "Schedule:", value: "Flexible timing" },
                    {
                      label: "Prerequisites:",
                      value: "Basic computer literacy",
                    },
                  ].map((detail, index) => (
                    <div
                      key={index}
                      className={`transform transition-all duration-500 hover:translate-x-2 ${
                        isVisible
                          ? "translate-y-0 opacity-100"
                          : "translate-y-2 opacity-0"
                      }`}
                      style={{
                        transitionDelay: `${1000 + index * 100}ms`,
                      }}
                    >
                      <span className="font-semibold text-gray-700">
                        {detail.label}
                      </span>
                      <span className="ml-2 text-gray-600">{detail.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Career Opportunities */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group">
                <h3 className="text-xl font-bold text-gray-800 mb-4 transition-colors duration-300 group-hover:text-custom-pink">
                  Career Opportunities
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    "Computer Repair Technician",
                    "Hardware Support Specialist",
                    "System Builder",
                    "IT Support Technician",
                    "Field Service Engineer",
                    "Hardware Sales Consultant",
                  ].map((career, index) => (
                    <li
                      key={index}
                      className={`hover:text-custom-pink transition-all duration-300 hover:translate-x-2 cursor-pointer transform ${
                        isVisible
                          ? "translate-y-0 opacity-100"
                          : "translate-y-2 opacity-0"
                      }`}
                      style={{
                        transitionDelay: `${1200 + index * 100}ms`,
                      }}
                    >
                      • {career}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Apply Now */}
              <div className="bg-custom-pink text-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group">
                <h3 className="text-xl font-bold mb-4 group-hover:scale-105 transition-transform duration-300">
                  Ready to Start?
                </h3>
                <p className="mb-4 opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                  Join our Computer Hardware program and build your expertise in
                  computer systems.
                </p>
                <button className="w-full bg-yellow-500 text-black py-3 px-6 rounded-lg font-semibold hover:bg-yellow-600 transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-95 mb-3">
                  Apply Now
                </button>
                <button className="w-full bg-transparent border-2 border-white text-white py-3 px-6 rounded-lg font-semibold hover:bg-white hover:text-custom-pink transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-95">
                  Request Info
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FullScreenImage />
      <Footer />
    </div>
  );
};

export default ComputerHardware;
